import type { TypographyVariantsOptions } from '@mui/material';

import { fontFamily, fontFamilyMono, fontSize, fontWeight } from './tokens';

/**
 * The design system's type scale as MUI variants.
 *
 * SOURCE: Figma "Design System v1" 4278:259, read with `get_variable_defs`. The
 * variant names below are Figma's own (`Geist Sans/Display1`, `Geist Sans/Label`,
 * …) mapped onto MUI's naming.
 *
 * Three things differ from `Web/src/assets/themes/typography.ts`, which had
 * drifted and which this file previously copied:
 *
 * - **`label` is 11px, and `labelMono` is 10px.** Web says 12 for both.
 * - **`display1` is 40/600 and `display2` is 20/500.** Web has display1 96/400,
 *   display2 40/400 and a third `display3` 20/400 that Figma does not define.
 * - **There is no 500-weight body.** Figma binds `semibold` (500) only to
 *   Display2; the emphasised body is `Body bold` at 14/600. Web's
 *   `bodySemiBold` (14/500) is a Web invention.
 *
 * `lineHeight: 'normal'` throughout is faithful — Figma sets `lineHeight: 100`
 * (i.e. 100%) and `letterSpacing: 0` on every single variant.
 */

const px = (size: number): string => `${size}px`;

const bodyRegular = {
  fontSize: px(fontSize.body),
  fontWeight: fontWeight.regular,
  lineHeight: 'normal',
};
const bodyLight = { ...bodyRegular, fontWeight: fontWeight.light };
const bodyBold = { ...bodyRegular, fontWeight: fontWeight.bold };

const label = {
  fontSize: px(fontSize.label),
  fontWeight: fontWeight.regular,
  lineHeight: 'normal',
};

const h1 = { fontSize: px(fontSize.h1), fontWeight: fontWeight.regular, lineHeight: 'normal' };
const h2 = { fontSize: px(fontSize.h2), fontWeight: fontWeight.regular, lineHeight: 'normal' };
const h3 = { fontSize: px(fontSize.h3), fontWeight: fontWeight.regular, lineHeight: 'normal' };
const h4 = { fontSize: px(fontSize.h4), fontWeight: fontWeight.regular, lineHeight: 'normal' };

/** Display1 — the largest heading. 40px semibold-to-bold. */
const display1 = {
  fontSize: px(fontSize.display1),
  fontWeight: fontWeight.bold,
  lineHeight: 'normal',
};
/** Display2 — H3's size at 500. This is the design system's "emphasised H3". */
const display2 = {
  fontSize: px(fontSize.display2),
  fontWeight: fontWeight.semibold,
  lineHeight: 'normal',
};

const mono = <T extends object>(variant: T): T & { fontFamily: string } => ({
  ...variant,
  fontFamily: fontFamilyMono,
});

export const typography: TypographyVariantsOptions = {
  fontFamily,
  fontSize: fontSize.body,

  // body1 is MUI's fallback for anything that does not name a variant.
  body1: bodyRegular,
  bodyRegular,
  bodyLight,
  bodyBold,
  label,
  h1,
  h2,
  h3,
  h4,
  display1,
  display2,

  /**
   * Mono twins. Metrics match their sans counterparts so columns of figures stay
   * aligned with the labels beside them — except `labelMono`, which Figma sets a
   * step smaller (10px) than the sans label.
   */
  bodyRegularMono: mono(bodyRegular),
  bodyLightMono: mono(bodyLight),
  bodyBoldMono: mono(bodyBold),
  labelMono: { ...mono(label), fontSize: px(fontSize.labelMono) },
  h1Mono: mono(h1),
  h2Mono: mono(h2),
  h3Mono: mono(h3),
  h4Mono: mono(h4),
  display1Mono: mono(display1),
  display2Mono: mono(display2),
};
